import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { siteMasterPaths } from '@/lib/siteMasterPaths';
import { SegmentedTabs } from '@/components/ui/SegmentedTabs';
import { MicroPromotionEntryFormModal } from '@/components/MicroPromotionEntryFormModal';
import { MicroPromotionEntryDetailModal } from '@/components/MicroPromotionEntryDetailModal';
import { cn } from '@/lib/cn';
import { mpServingStatusBadgeClass, mpServingStatusLabel } from '@/lib/microPromotionReviewUi';
import { mpEntryPreviewText } from '@/lib/microPromotionEntryText';

type AreaRow = { id: number; name: string; enabled: boolean };

type EntryRow = {
  entry_id: number;
  display_text?: string;
  promotion_text?: string;
  contact_phone?: string;
  promotion_serving_status?: string;
  serving_status_label?: string;
  matched_sensitive_words?: string[];
  source_label?: string;
  editable?: boolean;
  created_at?: string;
};

const PER = 20;

export function SiteMasterMpPage() {
  const { promotionAreas } = useSiteMasterSession();
  const areas = promotionAreas ?? [];
  const [areaId, setAreaId] = useState(0);
  const [infoType, setInfoType] = useState<'graphic' | 'recruitment'>('graphic');
  const [items, setItems] = useState<EntryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(0);
  const [busyId, setBusyId] = useState(0);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (areaId < 1 && areas.length > 0) {
      setAreaId(areas[0].id);
    }
  }, [areas, areaId]);

  const load = useCallback(async () => {
    if (areaId < 1) return;
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; items: EntryRow[] }>('site_master_list_mp_entries', {
        method: 'GET',
        query: { area_id: areaId, info_type: infoType, page: 1, per_page: PER },
      });
      if (r.ok) setItems(r.items);
      else setMsg('加载失败');
    } finally {
      setLoading(false);
    }
  }, [areaId, infoType]);

  useEffect(() => {
    void load();
  }, [load]);

  async function approveReview(id: number) {
    setBusyId(id);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; detail?: string }>('site_master_approve_micro_promotion_entry_review', {
        method: 'POST',
        body: JSON.stringify({ entry_id: id }),
      });
      if (!r.ok) {
        setMsg(r.detail || '审核失败');
        return;
      }
      setMsg('已通过审核');
      await load();
    } finally {
      setBusyId(0);
    }
  }

  async function removeEntry(id: number) {
    if (!window.confirm('确定删除该条推广信息？')) return;
    const r = await apiFetch<{ ok: boolean }>('site_master_delete_mp_entry', {
      method: 'POST',
      body: JSON.stringify({ entry_id: id }),
    });
    if (r.ok) await load();
    else setMsg('删除失败（金数据来源不可删）');
  }

  if (areas.length === 0) {
    return (
      <section className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-6 text-center">
        <p className="text-lg font-semibold text-amber-950">尚未分配微推广区域</p>
        <p className="mt-2 text-base text-amber-900">请联系管理员在「站长管理」中为您勾选推广区域。</p>
        <Link to={siteMasterPaths.home} className="mt-4 inline-block text-teal-700 underline">
          返回首页
        </Link>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-bold text-slate-900">微推广信息</h1>
          <Link to={siteMasterPaths.home} className="text-base font-semibold text-teal-700">
            返回
          </Link>
        </div>
        <p className="mt-2 text-base text-slate-600">与站点信息独立；类型与后台「微推广管理」一致。</p>
        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-600">推广区域</span>
          <select
            className="mt-1 w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-lg"
            value={areaId}
            onChange={(e) => setAreaId(parseInt(e.target.value, 10) || 0)}
          >
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      <SegmentedTabs
        ariaLabel="信息类型"
        value={infoType}
        onChange={setInfoType}
        items={[
          { id: 'graphic', label: '图文信息' },
          { id: 'recruitment', label: '招聘信息' },
        ]}
      />

      <button
        type="button"
        className="w-full rounded-2xl bg-teal-600 py-4 text-lg font-bold text-white shadow-md active:bg-teal-700"
        onClick={() => {
          setEditId(0);
          setFormOpen(true);
        }}
      >
        添加{infoType === 'graphic' ? '图文' : '招聘'}信息
      </button>

      {msg ? <p className="text-center text-base text-rose-600">{msg}</p> : null}

      {loading ? (
        <p className="text-center text-slate-500">加载中…</p>
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-500">
          暂无信息，可点击上方按钮添加
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((row) => {
            const text = mpEntryPreviewText(row);
            const status = row.promotion_serving_status || 'active';
            const preview = text.length > 80 ? text.slice(0, 80) + '…' : text;
            return (
              <li key={row.entry_id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-slate-500">#{row.entry_id}</span>
                    <span className="ml-2 text-xs text-teal-700">{row.source_label}</span>
                    <p className="mt-2 text-base leading-snug text-slate-900" title={text}>
                      {preview || '（无文案）'}
                    </p>
                    {row.contact_phone ? <p className="mt-1 text-sm text-slate-600">{row.contact_phone}</p> : null}
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1',
                      mpServingStatusBadgeClass(status),
                    )}
                  >
                    {mpServingStatusLabel(status, row.serving_status_label)}
                  </span>
                </div>
                {status === 'pending_review' && (row.matched_sensitive_words?.length ?? 0) > 0 ? (
                  <p className="mt-2 text-sm text-violet-800">
                    敏感词：{row.matched_sensitive_words!.slice(0, 4).join('、')}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="flex-1 min-w-[6rem] rounded-xl border-2 border-slate-200 py-2 text-base font-semibold text-slate-800"
                    onClick={() => {
                      setDetailId(row.entry_id);
                      setDetailOpen(true);
                    }}
                  >
                    详情
                  </button>
                  {status === 'pending_review' ? (
                    <button
                      type="button"
                      disabled={busyId === row.entry_id}
                      className="flex-1 min-w-[6rem] rounded-xl bg-teal-600 py-2 text-base font-bold text-white disabled:opacity-60"
                      onClick={() => void approveReview(row.entry_id)}
                    >
                      通过审核
                    </button>
                  ) : null}
                </div>
                {row.editable !== false ? (
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className="flex-1 rounded-xl border-2 border-slate-200 py-2 text-base font-semibold text-slate-800"
                      onClick={() => {
                        setEditId(row.entry_id);
                        setFormOpen(true);
                      }}
                    >
                      编辑
                    </button>
                    <button
                      type="button"
                      className="flex-1 rounded-xl border-2 border-rose-200 py-2 text-base font-semibold text-rose-700"
                      onClick={() => void removeEntry(row.entry_id)}
                    >
                      删除
                    </button>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">金数据提交，仅可查看</p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <MicroPromotionEntryFormModal
        open={formOpen}
        areaId={areaId}
        infoType={infoType}
        entryId={editId}
        siteMasterMode
        onClose={() => {
          setFormOpen(false);
          setEditId(0);
        }}
        onSaved={() => void load()}
      />
      <MicroPromotionEntryDetailModal
        open={detailOpen}
        entryId={detailId}
        siteMasterMode
        onClose={() => {
          setDetailOpen(false);
          setDetailId(0);
        }}
        onReviewChanged={() => void load()}
      />
    </div>
  );
}
